import { useMemo, useState } from 'react'
import { Trophy as TrophyIcon } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { tierStyle } from './trophyShared'
import type { OriginalTrophy } from '@/api/trophies'

type SortKey = 'date' | 'rarityDesc' | 'rarityAsc'

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'date', label: '最近生成した' },
  { value: 'rarityDesc', label: 'レア度が高い順' },
  { value: 'rarityAsc', label: 'レア度が低い順' },
]

/** 未配置のトロフィーから選んでスロットに配置する */
export default function TrophyPickerDialog({
  open,
  candidates,
  placedIds,
  onSelect,
  onClose,
}: {
  open: boolean
  candidates: OriginalTrophy[]
  placedIds?: Set<string>
  onSelect: (t: OriginalTrophy) => void
  onClose: () => void
}) {
  const [q, setQ] = useState('')
  const [sort, setSort] = useState<SortKey>('date')

  const displayed = useMemo(() => {
    const list = q
      ? candidates.filter((t) => t.name.includes(q) || t.title.includes(q))
      : [...candidates]

    list.sort((a, b) => {
      switch (sort) {
        case 'date':
          return b.created_at.localeCompare(a.created_at)
        case 'rarityDesc':
          return b.rarity - a.rarity
        case 'rarityAsc':
          return a.rarity - b.rarity
      }
    })
    return list
  }, [q, candidates, sort])

  const handleClose = () => {
    setQ('')
    setSort('date')
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose() }}>
      <DialogContent className="flex h-[80vh] flex-col">
        <DialogHeader>
          <DialogTitle>トロフィーを飾る</DialogTitle>
        </DialogHeader>

        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="トロフィー名・称号で検索"
          autoComplete="off"
        />

        <div className="mt-2 flex gap-1.5 overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden">
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setSort(opt.value)}
              className={`shrink-0 rounded-full border px-3 py-1 text-xs transition-colors ${
                sort === opt.value
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-background text-muted-foreground'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="flex-1 space-y-1.5 overflow-y-auto">
          {displayed.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {candidates.length === 0 ? '飾れるトロフィーがありません' : '該当なし'}
            </p>
          ) : (
            displayed.map((t) => {
              const { color, glow } = tierStyle(t.rarity)
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onSelect(t)}
                  className="flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left hover:bg-muted/50"
                >
                  <TrophyIcon size={24} style={{ color, filter: `drop-shadow(0 0 4px ${glow})` }} className="shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{t.name}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {t.title && `${t.title}・`}レア度 {t.rarity}
                    </p>
                  </div>
                  {placedIds?.has(String(t.id)) && (
                    <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">飾り済み</span>
                  )}
                </button>
              )
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
